import { supabase } from "@/integrations/supabase/client";

export interface SystemHealth {
  drip: {
    status: "configured" | "not_configured" | "needs_attention";
    shopIdPresent: boolean;
    enabled: boolean;
  };
  smtp: {
    status: "configured" | "disabled" | "needs_attention";
    enabled: boolean;
    hostPresent: boolean;
    usernamePresent: boolean;
  };
  notifications: {
    status: "configured" | "missing_templates";
    jobCreatedTemplate: boolean;
    jobStatusTemplate: boolean;
  };
}

export async function getSystemHealth(): Promise<SystemHealth> {
  // Check Drip configuration
  const { data: dripSettings } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", "drip_enabled")
    .single();

  const dripEnabled = dripSettings?.value === "true";
  const { data: dripShopId } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", "DRIP_SHOP_ID")
    .single();

  const dripShopIdPresent = !!dripShopId?.value;
  const dripStatus: SystemHealth["drip"]["status"] =
    dripEnabled && dripShopIdPresent ? "configured" : "not_configured";

  // Check SMTP configuration
  const { data: emailSettings } = await supabase.from("email_settings").select("*").single();

  const smtpEnabled = emailSettings?.smtp_enabled ?? false;
  const smtpHostPresent = !!emailSettings?.smtp_host;
  const smtpUsernamePresent = !!emailSettings?.smtp_username;
  const smtpStatus: SystemHealth["smtp"]["status"] =
    smtpEnabled && smtpHostPresent && smtpUsernamePresent
      ? "configured"
      : smtpEnabled
        ? "needs_attention"
        : "disabled";

  // Check email templates
  const { data: templates } = await supabase
    .from("email_templates")
    .select("key")
    .in("key", ["job_created_customer", "job_status_changed_customer"]);

  const jobCreatedTemplate = templates?.some((t) => t.key === "job_created_customer") ?? false;
  const jobStatusTemplate =
    templates?.some((t) => t.key === "job_status_changed_customer") ?? false;
  const notificationsStatus: SystemHealth["notifications"]["status"] =
    jobCreatedTemplate && jobStatusTemplate ? "configured" : "missing_templates";

  return {
    drip: {
      status: dripStatus,
      shopIdPresent: dripShopIdPresent,
      enabled: dripEnabled,
    },
    smtp: {
      status: smtpStatus,
      enabled: smtpEnabled,
      hostPresent: smtpHostPresent,
      usernamePresent: smtpUsernamePresent,
    },
    notifications: {
      status: notificationsStatus,
      jobCreatedTemplate,
      jobStatusTemplate,
    },
  };
}
